import { jsx } from 'react/jsx-runtime';
import React, { useMemo } from 'react';
import { DEFAULT_LOCALE } from '../constants/locale.js';
import { resources } from '../localization/index.js';
import { Span } from '@vp/swan';

const mergeNestedObjects = (target, source) => {
  const result = { ...target };
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      result[key] = typeof sourceValue === "object" && sourceValue !== null ? mergeNestedObjects(result[key] ?? {}, sourceValue) : sourceValue;
    }
  }
  return result;
};
const getLocalizationsFor = (locale) => {
  const resourcesObj = resources[locale.toLowerCase()];
  if (!resourcesObj) {
    console.warn(`Localization for ${locale} not found, falling back to default locale ${DEFAULT_LOCALE}.`);
    return resources[DEFAULT_LOCALE.toLowerCase()];
  }
  return mergeNestedObjects(resources[DEFAULT_LOCALE.toLowerCase()], resourcesObj);
};
const getLocalizationForKey = (localizations, path) => {
  return path.split(".").reduce((currentLocalizationObj, pathSegment) => {
    if (currentLocalizationObj && typeof currentLocalizationObj === "object" && pathSegment in currentLocalizationObj) {
      return currentLocalizationObj[pathSegment];
    }
    return void 0;
  }, localizations);
};
const useLocalization = (locale = DEFAULT_LOCALE) => {
  const localizations = useMemo(() => getLocalizationsFor(locale), [locale]);
  const t = (key, tokens = {}) => {
    const localizedText = getLocalizationForKey(localizations, key);
    if (!localizedText) {
      console.error(`Localization for key ${key} not found`);
      return key;
    }
    const parts = localizedText.split(/{{\s*(.*?)\s*}}/g);
    const renderedParts = parts.map((part) => tokens[part] || part);
    const isReactNode = renderedParts.some(React.isValidElement);
    const renderedPartsWithKeys = isReactNode ? parts.map((part, index) => {
      const value = tokens[part] || part;
      if (React.isValidElement(value)) {
        return React.cloneElement(value, { key: `part-${index}` });
      }
      return /* @__PURE__ */ jsx(React.Fragment, { children: value }, `part-${index}`);
    }) : null;
    return isReactNode ? /* @__PURE__ */ jsx(Span, { children: renderedPartsWithKeys }) : renderedParts.join("");
  };
  return { t };
};

export { useLocalization };
